import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertTriangle, BriefcaseBusiness, CalendarDays, ChevronRight, ClipboardCheck, FileText, FolderOpen, MessageSquare,
} from 'lucide-react';
import { adminApi } from '../services/api';
import { Carte, Chargement, fmtDate, fmtDateHeure, Pastille, STATUTS_SEANCE, Vide } from '../components/suivi/ui';
import MesDocuments from '../components/MesDocuments';
import { EtatErreur, Squelette } from '../etats';

function Compteur({ icone: Icone, libelle, valeur, alerte }) {
  return (
    <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-pastille flex items-center justify-center shrink-0 ${alerte && valeur ? 'bg-[var(--attention-fond)] text-[var(--attention)]' : 'bg-[var(--bg-main)] text-[var(--accent-texte)]'}`}>
        <Icone className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-semibold text-[var(--text-bold)] tabular-nums leading-none">{valeur}</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">{libelle}</p>
      </div>
    </div>
  );
}

/* Portefeuille du coach : ses associations, ce qui l'attend, ses rendez-vous. */
export default function EspaceCoach({ user }) {
  const navigate = useNavigate();
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [essai, setEssai] = useState(0);
  /* Deux choses distinctes, et les mêler aurait noyé l'une dans l'autre :
     le PORTEFEUILLE dit ce qui attend le coach chez ses associations ;
     MES DOCUMENTS sont ses pièces à lui — fiches, relevés, évaluations —
     qui n'appartiennent à aucun projet en particulier. */
  const [vue, setVue] = useState('portefeuille');

  useEffect(() => {
    let vivant = true;
    setErreur(null);
    // L'objet entier : son statut dit lequel des cas afficher.
    adminApi.getTableauCoach().then((d) => vivant && setDonnees(d)).catch((e) => vivant && setErreur(e));
    return () => { vivant = false; };
  }, [essai]);

  if (erreur && !donnees) {
    return (
      <div className="bg-[var(--bg-card)] rounded-carte shadow-carte">
        <EtatErreur erreur={erreur} onReessayer={() => setEssai((x) => x + 1)} />
      </div>
    );
  }
  // Premier chargement : la forme du tableau de bord, compteurs compris —
  // un « 0 » affiché en attendant se lirait comme un chiffre.
  if (!donnees) {
    return (
      <div className="space-y-6" role="status" aria-label="Chargement de votre espace">
        <div>
          <Squelette className="h-3 w-28 mb-3" />
          <Squelette className="h-8 w-64 mb-3" />
          <Squelette className="h-3 w-80 max-w-full" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-[var(--bg-card)] rounded-carte shadow-carte p-4 flex items-center gap-3">
              <Squelette className="w-10 h-10 !rounded-pastille shrink-0" />
              <div className="flex-1"><Squelette className="h-6 w-10 mb-2" /><Squelette className="h-2.5 w-24" /></div>
            </div>
          ))}
        </div>
        <div className="grid xl:grid-cols-3 gap-4 items-start">
          <div className="xl:col-span-2"><Chargement /></div>
          <Chargement forme="texte" />
        </div>
      </div>
    );
  }
  const { compteurs: n, projets, seances, rapports_brouillons: brouillons } = donnees;
  const ouvrir = (projetId, onglet) => navigate(`/projets/${projetId}${onglet ? `?onglet=${onglet}` : ''}`);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
      <header>
        <p className="sous-titre-page">Mon espace coach</p>
        <h1 className="titre-page">Bonjour {user?.first_name || user?.username}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {n.projets ? `${n.projets} association(s) suivie(s), dont ${n.en_cours} en cours d'accompagnement.` : "Aucune association ne vous est encore confiée."}
        </p>
      </header>

      <div className="flex gap-1 flex-wrap border-b border-[var(--border)] pb-px">
        {[
          { cle: 'portefeuille', libelle: 'Mon portefeuille' },
          { cle: 'documents', libelle: 'Mes documents' },
        ].map((o) => {
          const actif = vue === o.cle;
          return (
            <button
              key={o.cle}
              type="button"
              onClick={() => setVue(o.cle)}
              style={{ minHeight: 44,
                       color: actif ? 'var(--text-bold)' : 'var(--text-muted)',
                       boxShadow: actif ? 'inset 0 -2px 0 var(--accent)' : 'none' }}
              className="act act-tertiaire"
            >
              {o.libelle}
            </button>
          );
        })}
      </div>

      {vue === 'documents' ? <MesDocuments /> : (
        <>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Compteur icone={CalendarDays} libelle="Séances à venir" valeur={n.seances_a_venir} />
        <Compteur icone={MessageSquare} libelle="Messages non lus" valeur={n.messages_non_lus} alerte />
        <Compteur icone={FolderOpen} libelle="Documents à valider" valeur={n.documents_a_valider} alerte />
        <Compteur icone={AlertTriangle} libelle="Actions en retard" valeur={n.actions_en_retard} alerte />
      </div>

      <div className="grid xl:grid-cols-3 gap-4 items-start">
        <Carte titre="Mes associations" className="xl:col-span-2">
          {projets.length === 0 ? (
            <Vide icone={BriefcaseBusiness} titre="Aucune association confiée" texte="L'administration vous attribue les projets d'accompagnement : ils apparaîtront ici." />
          ) : (
            <ul className="divide-y divide-[var(--border)]">
              {projets.map((p) => {
                const pct = p.actions_total ? Math.round((p.actions_terminees / p.actions_total) * 100) : 0;
                return (
                  <li key={p.id}>
                    <button type="button" onClick={() => ouvrir(p.id)} className="w-full text-left py-3 flex items-center gap-3 group">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-[var(--text-bold)] group-hover:text-[var(--accent-texte)]">{p.organisation}</span>
                          <span className="text-xs text-[var(--text-muted)]">{p.type} · {p.statut_libelle}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex-1 h-1.5 rounded-controle bg-[var(--bg-main)] overflow-hidden" aria-hidden="true">
                            <div className="h-full bg-[var(--accent)]" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-[var(--text-muted)] tabular-nums shrink-0">{p.actions_terminees}/{p.actions_total} actions</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2 text-xs">
                          {p.dernier_diagnostic && (
                            <span className="text-[var(--text-muted)]">
                              Diagnostic {p.dernier_diagnostic.type.toLowerCase()} : {p.dernier_diagnostic.score_global != null ? `${p.dernier_diagnostic.score_global} % · ${p.dernier_diagnostic.niveau}` : p.dernier_diagnostic.statut === 'en_cours' ? 'réponses en cours' : 'ouvert'}
                            </span>
                          )}
                          {p.prochaine_seance && <span className="text-[var(--text-main)]">· Prochaine séance {fmtDateHeure(p.prochaine_seance.date_debut)}</span>}
                          {p.messages_non_lus > 0 && <Pastille className="text-[var(--text-bold)] bg-[var(--info-fond)]">{p.messages_non_lus} message(s)</Pastille>}
                          {p.documents_a_valider > 0 && <Pastille className="text-[var(--attention)] bg-[var(--attention-fond)]">{p.documents_a_valider} document(s)</Pastille>}
                          {p.actions_en_retard > 0 && <Pastille className="text-[var(--alerte)] bg-[var(--alerte-fond)]">{p.actions_en_retard} en retard</Pastille>}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Carte>

        <div className="space-y-4">
          <Carte titre="Prochaines séances">
            {seances.length === 0 ? <p className="text-sm text-[var(--text-muted)]">Aucune séance programmée.</p> : (
              <ul className="space-y-3">
                {seances.map((s) => {
                  const statut = STATUTS_SEANCE[s.statut] || STATUTS_SEANCE.proposee;
                  return (
                    <li key={s.id}>
                      <button type="button" onClick={() => ouvrir(s.projet_id, 'seances')} className="w-full text-left rounded-pastille p-3 bg-[var(--bg-main)] hover:ring-1 hover:ring-[var(--accent)]">
                        <p className="text-sm font-semibold text-[var(--text-bold)]">{fmtDateHeure(s.date_debut)}</p>
                        <p className="text-sm text-[var(--text-main)]">{s.objet}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="text-xs text-[var(--text-muted)]">{s.organisation}</span>
                          <Pastille className={statut.className}>{statut.label}</Pastille>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Carte>
          <Carte titre="À faire">
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-[var(--text-main)]"><ClipboardCheck className="w-4 h-4 text-[var(--accent-texte)]" />{n.diagnostics_a_cloturer} diagnostic(s) en attente de clôture</li>
              <li className="flex items-center gap-2 text-[var(--text-main)]"><FileText className="w-4 h-4 text-[var(--accent-texte)]" />{n.rapports_brouillons} rapport(s) en brouillon</li>
            </ul>
            {brouillons.length > 0 && (
              <ul className="mt-3 space-y-2">
                {brouillons.map((r) => (
                  <li key={r.id}>
                    <button type="button" onClick={() => ouvrir(r.projet_id, 'rapports')} className="text-left text-sm text-[var(--accent-texte)] font-semibold hover:underline">
                      {r.titre}
                    </button>
                    <span className="block text-xs text-[var(--text-muted)]">{r.organisation} · modifié le {fmtDate(r.updated_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Carte>
        </div>
      </div>
        </>
      )}
    </motion.div>
  );
}